import { Store, GetterTree, MutationTree, ActionTree, Module } from "vuex";

import { RootState } from "..";
import { propertyService } from "../../services/property.service";
import { propertyProductService } from "@/services/property-product.service";
import { IPropertyManagementState } from "../states/property-management-state";
import { Property } from "../../models/property/property.model";
import { IProperty } from "../../models/interfaces/property/property.model.interface";
import { IPropertyDetailsRequest } from "../../models/interfaces/property/property-details-request.model.interface";
import { IPropertyBaseDetailsRequest } from "@/models/interfaces/property/property-base-details-request.model.interface";
import { IPropertyBlockBrand } from "@/models/interfaces/property/property-block-brand.model.interface";
import { IProductCategory } from "@/models/interfaces/product/product-category.model.interface";
import { IAdvertisingControlsRequest } from "@/models/interfaces/property/advertising-controls-request.model";
import { StatusType } from "@/enums/StatusType";

export const state: IPropertyManagementState = {
    property: new Property()
};

export const getters: GetterTree<IPropertyManagementState, RootState> = {
    property(state) {
        return state.property;
    }
};

export const mutations: MutationTree<IPropertyManagementState> = {
    property(state, property: IProperty) {
        state.property = property;
    },
    addProductCategory(state, productCategory: IProductCategory) {
        state.property.permittedCategories.push(productCategory);
    },
    removeProductCategory(state, productCategoryId: number) {
        state.property.permittedCategories = state.property.permittedCategories.filter((item) => item.productCategoryId !== productCategoryId);
    },
    addBlockedBrand(state, blockedBrand: IPropertyBlockBrand) {
        state.property.blockedBrands.push(blockedBrand);
    },
    removeBlockedBrand(state, blockedBrand: IPropertyBlockBrand) {
        state.property.blockedBrands = state.property.blockedBrands.filter((item) =>
            !(item.productCategoryId === blockedBrand.productCategoryId
                && item.brandId === blockedBrand.brandId));
    }
};

export const actions: ActionTree<IPropertyManagementState, RootState> = {
    async createProperty({ commit, rootState }, property: IPropertyDetailsRequest) {
        return new Promise((resolve) => {
            propertyService.create(
                rootState.publisherManagementModule.publisher.publisherId,
                property)
                .then((value) => {
                    if (value.success && value.result !== null) {
                        commit("property", value.result);
                    }
                    resolve(value);
                });
        });
    },
    async retrieveProperty({ commit }, propertyId: number) {
        return new Promise((resolve, reject) => {
            propertyService.getProperty(propertyId)
                .then((value) => {
                    if (value.success) {
                        commit("property", value.result);
                    }
                    resolve(value.success);
                });
        });
    },
    async updateDetails({ state }, property: IProperty) {
        return new Promise((resolve, reject) => {
            const request: IPropertyBaseDetailsRequest = {
                name: property.name,
                productCategoryThemeId: property.productCategoryThemeId
            };

            propertyService.updateDetails(property.propertyId, request).then((value) => {
                resolve(value);
            });
        });
    },
    async pauseProperty({ dispatch, rootState }, property: IProperty) {
        return new Promise((resolve) => {
            property.state.statusType = StatusType.Paused;
            propertyService.updateState(property.propertyId, property.state).then((value) => {
                dispatch("publisherManagementModule/retrievePublisher", rootState.publisherManagementModule.publisher.publisherId, { root: true }).then((value) => {
                    resolve(true);
                });
            });
        });
    },
    async setPropertyLive({ dispatch, rootState }, property: IProperty) {
        return new Promise((resolve) => {
            property.state.statusType = StatusType.Live;
            propertyService.updateState(property.propertyId, property.state).then((value) => {
                dispatch("publisherManagementModule/retrievePublisher", rootState.publisherManagementModule.publisher.publisherId, { root: true }).then((value) => {
                    resolve(true);
                });
            });
        });
    },
    async updateAdvertisingControls({ state }, property: IProperty) {
        return new Promise((resolve, reject) => {
            const request: IAdvertisingControlsRequest = {
                allowConquest: property.allowConquest
            };
            propertyService.updateAdvertisingControls(property.propertyId, request).then((value) => {
                resolve(value);
            });
        });
    },
    async addProductCategory({ commit, state, rootState }, productCategoryId: number) {
        return new Promise((resolve) => {
            propertyProductService.addProductCategory(state.property.propertyId, productCategoryId)
                .then((value) => {
                    if (value) {
                        commit("addProductCategory", rootState.productModule.productCategories.filter((item) => item.productCategoryId === productCategoryId)[0]);
                        resolve(value);
                    } else {
                        resolve(false);
                    }
                });
        });
    },
    async removeProductCategory({ commit, state }, productCategoryId: number) {
        return new Promise((resolve) => {
            propertyProductService.removeProductCategory(state.property.propertyId, productCategoryId)
                .then((value) => {
                    if (value) {
                        commit("removeProductCategory", productCategoryId);
                        resolve(value);
                    } else {
                        resolve(false);
                    }
                });
        });
    },
    async addBlockedBrand({ commit, state, rootState }, blockedBrand: IPropertyBlockBrand) {
        return new Promise((resolve) => {
            propertyProductService.addBlockedBrand(blockedBrand)
                .then((value) => {
                    if (value) {
                        commit("addBlockedBrand", blockedBrand);
                        resolve(value);
                    } else {
                        resolve(false);
                    }
                });
        });
    },
    async removeBlockedBrand({ commit, state }, blockedBrand: IPropertyBlockBrand) {
        return new Promise((resolve) => {
            propertyProductService.removeBlockedBrand(blockedBrand)
                .then((value) => {
                    if (value) {
                        commit("removeBlockedBrand", blockedBrand);
                        resolve(value);
                    } else {
                        resolve(false);
                    }
                });
        });
    }
};

export const propertyManagementModule: Module<IPropertyManagementState, RootState> = {
    state, getters, mutations, actions, namespaced: true
};
